/**
 * Checkbox-based filter options panel with fuzzy search support.
 * @module FilterOptions
 */

//TODO: make the file smaller by breaking into smaller ones
import { CheckboxList } from "@risalabs_frontend_org/oasis-ui-kit";
import Fuse from "fuse.js";
import { useEffect, useMemo, useState } from "react";
import { logDataToConsole } from "../../utils/customLogger";
import SearchBar from "../searchBar/searchBar";

/** Props for the {@link FilterOptions} component. */
interface FilterOptionsProps {
  id: string;
  label: string;
  options: { label: string; value: string }[];
  onCheckBoxValueChange: (data: {
    id: string;
    values: string[];
    valuesLabel: string[];
    type: "string" | "number" | "date";
  }) => void;
  defaultValues?: string[];
  type?: "string" | "number" | "date";
}

const fuzzySearchOptions = {
  includeScore: true,
  threshold: 0.3,
  distance: 200,
  useExtendedSearch: true,
  keys: ["label"],
};

/**
 * Renders a searchable checkbox list for a single filter section.
 * Supports select-all, clear-all, and fuzzy search (Fuse.js) over option labels.
 *
 * @param props - {@link FilterOptionsProps}
 */
const FilterOptions = (props: FilterOptionsProps) => {
  const [searchedText, setSearchedText] = useState("");
  const [visibleData, setVisibleData] = useState<
    { label: string; value: string }[]
  >([]);
  const [checkedValues, setCheckedValues] = useState<{
    [key: string]: boolean;
  }>({});
  const [resetSearchBar] = useState(false);

  // Initialize checked values when props.id changes (filter changes) or when options/defaultValues change
  useEffect(() => {
    // Initialize checked values based on defaultValues and available options
    const initialCheckedValues = Object.fromEntries(
      props.options.map((item) => [
        item.value,
        props.defaultValues?.includes(item.value) ?? false,
      ]),
    );

    const valueesWithOnlyTrue = Object.keys(initialCheckedValues).filter(
      (key) => initialCheckedValues[key] === true,
    );

    setCheckedValues(
      Object.fromEntries(valueesWithOnlyTrue.map((key) => [key, true])),
    );
  }, [props.id, props.options, props.defaultValues]);

  useEffect(() => {
    if (props.options && props.options.length > 0) {
      setVisibleData(props.options);
    }
  }, [props.options]);

  /**
   * Handles individual checkbox toggle events from the CheckboxList.
   *
   * @param items - Current state of all checkbox items
   * @param changedId - ID of the checkbox that was toggled
   */
  const handleSelectionChange = (
    items: { id: string; label: string; checked: boolean }[],
    changedId: string,
  ) => {
    const updatedCheckedValues = {
      ...checkedValues,
      ...Object.fromEntries(items.map((item) => [item.id, item.checked])),
    };

    const checkedKeys = Object.keys(updatedCheckedValues).filter(
      (key) => updatedCheckedValues[key],
    );

    const selectedLabels = checkedKeys.map(
      (value) => optionMap[value] || value,
    );

    const dataToEmit = {
      id: props.id,
      values: checkedKeys,
      valuesLabel: selectedLabels,
      type: props.type ?? "string",
    };

    props.onCheckBoxValueChange(dataToEmit);
    logDataToConsole("Checked Values", dataToEmit);
    logDataToConsole("Changed checkbox", changedId);
  };

  /** Marks all currently visible options as checked and emits the change. */
  const handleSelectAllClicked = () => {
    const updatedCheckedValues = Object.fromEntries(
      visibleData.map((item) => [item.value, true]),
    );
    const newCheckedValues = { ...checkedValues, ...updatedCheckedValues };

    const dataToEmit = {
      id: props.id,
      values: Object.keys(newCheckedValues),
      valuesLabel: Object.keys(newCheckedValues).map(
        (key) => optionMap[key] || key,
      ),
      type: props.type ?? "string",
    };
    props.onCheckBoxValueChange(dataToEmit);
  };

  /** Unchecks all currently visible options and emits the change. */
  const handleClearAllClicked = () => {
    const updatedCheckedValuesToBeFalse = Object.fromEntries(
      visibleData.map((item) => [item.value, false]),
    );
    const updatedCheckedValues = {
      ...checkedValues,
      ...updatedCheckedValuesToBeFalse,
    };
    const dataToEmit = {
      id: props.id,
      values: Object.keys(updatedCheckedValues).filter(
        (key) => updatedCheckedValues[key] === true,
      ),
      valuesLabel: Object.keys(updatedCheckedValues)
        .filter((key) => updatedCheckedValues[key] === true)
        .map((key) => optionMap[key] || key),
      type: props.type ?? "string",
    };
    props.onCheckBoxValueChange(dataToEmit);
  };

  const optionMap = useMemo(
    () =>
      Object.fromEntries(props.options.map((opt) => [opt.value, opt.label])),
    [props.options],
  );

  useEffect(() => {
    if (searchedText.trim().length >= 3) {
      const fuse = new Fuse(props.options, fuzzySearchOptions);
      const result = fuse.search(searchedText);
      setVisibleData(result.map((data) => data.item));
    } else {
      setVisibleData(props.options);
    }
  }, [searchedText, props.options]);

  return (
    <div className="filter-options-container flex h-full flex-col gap-2 overflow-hidden px-5">
      <div className="filter-options--header mt-4 text-body font-semibold text-primaryGray-2">
        {props.label}
      </div>
      <div className="filter-options--actions flex justify-end gap-3">
        <div
          className="filter-options--actions__select-all text-small font-semibold text-tertiaryBlue-4 hover:cursor-pointer hover:shadow-sm"
          onClick={handleSelectAllClicked}
        >
          Select All
        </div>
        <div
          className="filter-options--actions__clear-all text-small font-semibold text-tertiaryRed-4 hover:cursor-pointer hover:shadow-sm"
          onClick={handleClearAllClicked}
        >
          Clear Selection
        </div>
      </div>
      <div className="filter-options--body flex flex-1 flex-col overflow-hidden">
        <div className="filter-options--body__search mb-2">
          <SearchBar
            placeHolder={"Search"}
            onSearchClick={(searchedString: string) =>
              setSearchedText(searchedString)
            }
            id={`filter-search-${props.id}`}
            enableSearchOnChange={true}
            onReset={resetSearchBar}
          />
        </div>
        <div className="filter-options--body__options flex-1 overflow-auto">
          <CheckboxList
            items={visibleData.map((item) => ({
              id: item.value,
              label: item.label,
              checked: checkedValues[item.value] === true,
            }))}
            onSelectionChange={handleSelectionChange}
            showSelectAll={false}
            className="custom-checkbox-for-filter-options"
            width={"100%"}
          />
        </div>
      </div>
    </div>
  );
};

export default FilterOptions;
